import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { Button, Callout, ConfirmButton, SectionCard, useToast } from '@/components/ui';
import {
    confirmAppSecretRotation,
    normalizeApiErrorDetails,
    provisionAppExternalSystem,
    updateAdminApp,
} from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';
import { AppUriListField } from '../Apps/AppUriListField';
import { AppCredentialReveal, type RevealedField } from './AppCredentialReveal';
import { AppDetailClientsCard } from './AppDetailClientsCard';
import { AppProvisioningResultModal } from './AppProvisioningResultModal';
import { useProvisioningResult } from './useProvisioningResult';
import { ConnectionCheckResult } from './ConnectionCheckResult';
import { oidcEndpointsFor } from './oidcEndpoints';
import { useCopyToClipboard } from './useCopyToClipboard';
import { ValueRow } from './ValueRow';
import type { AppDetailPageVM } from './useAppDetailPage';
import detail from './AppDetail.module.css';
import styles from './AppDetailConnectionTab.module.css';

/**
 * EAA-1817 (frame 50): Kết nối kỹ thuật tab.
 *
 * EAA-1810: this tab is now the screen an operator can actually connect an
 * EXTERNAL application from, end to end, rather than a read-only summary of a
 * connection someone else established over curl. Both credential channels an
 * external app can use are provisioned here:
 *
 *   - OIDC (`client_id` + `client_secret`, `POST /admin/apps/{code}/client`) —
 *     for a PERSON signing in, and for anything the app drives with the
 *     client-credentials grant. The tab previously rendered a row of bullet
 *     characters and a "last rotated" date with NO action beside it, so the one
 *     thing the screen existed to deliver could not be obtained from it.
 *   - HMAC (`system_code` + `shared_secret`,
 *     `POST /admin/apps/{code}/external-system`) — for the app's own BACKEND
 *     calling ours with no user present. `Application.external_system` was
 *     settable through the Django ORM alone before this, so an app could be
 *     registered, credentialed, approved, and still unable to make one
 *     server-to-server call.
 *
 * Both reveal their secret exactly once (D12) through `AppCredentialReveal`,
 * which keeps the value in component state only — no query cache, no refetch
 * path. Both actions are gated on `console.apps.credentials`, which is the key
 * the SERVER enforces (`Permission.APPS_CREDENTIALS`); the tab used to gate the
 * connection check on `console.apps.manage` as a best-effort stand-in, and that
 * is corrected here now that a real capability is wired through the VM.
 *
 * Also added: the OIDC endpoint set (derived from the realm issuer — see
 * `oidcEndpoints.ts`) and the HMAC signing recipe, because an operator's next
 * question after "where is the secret" is "what do I send them with it", and
 * the answer used to live only in
 * `services/portal-api/docs/overview/GUIDE-External-App-Integration.md`.
 *
 * EAA-1709: the login client is public (PKCE) and has no secret; the
 * "Thông tin kết nối" card (`AppDetailClientsCard`) now shows it next to the
 * app backend's machine client `<code>-api`, which is what "Cấp lại chuỗi bí
 * mật" re-issues — hidden for web apps. The check card shares its result with
 * the header's approval gate (`vm.approval`), so a passing check here also
 * unlocks "Duyệt".
 *
 * Still omitted vs the mock, with no fabricated substitute: "Cách cấp phiên"
 * (grant types + session TTL — no field on `Application` backs any of it, gap
 * G4) and the redirect-URI environment tags (`redirect_uris` is a flat
 * `string[]`, gap G5).
 */

/**
 * The server's own explanation of a failed provisioning call, when it gave one.
 * Preferred over a generic string: "Keycloak admin credentials not configured"
 * and "that redirect URI is invalid" are the same HTTP status and completely
 * different jobs for the operator.
 */
function provisioningDetail(error: unknown): string | null {
    if (!(error instanceof OrgAdminApiError)) return null;
    const details = normalizeApiErrorDetails(error.details);
    if (error.code === PROVISIONING_FAILED_CODE) return details.keycloak?.[0] ?? null;
    return details.redirect_uris?.[0] ?? null;
}

/** `EXT_PROVISIONING_FAILED` — a misconfigured realm, not a bad request. */
const PROVISIONING_FAILED_CODE = 6006;

function noop() {}

interface Revealed {
    fields: RevealedField[];
    warning: string;
    rotated: boolean;
    overlapExpiresAt?: string | null;
}

export function AppDetailConnectionTab({ vm }: { vm: AppDetailPageVM }) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();
    const app = vm.app;

    const [redirectUris, setRedirectUris] = useState<string[]>(app?.redirect_uris ?? ['']);
    const [postLogoutUris, setPostLogoutUris] = useState<string[]>(
        app?.post_logout_redirect_uris ?? ['']
    );

    // EAA-1810: the ONLY place either plaintext secret ever lives. Never written
    // to a query cache, never refetched — dismissing or leaving the tab loses it
    // for good, which is the contract, not a limitation to work around.
    const [revealedHmac, setRevealedHmac] = useState<Revealed | null>(null);
    // EAA-1709: a redirect-URI save is a PATCH, which may provision clients and
    // return a one-time `<code>-api` secret or a failed step — shown, not dropped.
    const provisioningResult = useProvisioningResult(noop);
    const copy = useCopyToClipboard();

    function invalidateApps() {
        void queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
        void queryClient.invalidateQueries({ queryKey: ['app-checklist', vm.code] });
    }

    const saveMutation = useMutation({
        mutationFn: (body: { redirect_uris?: string[]; post_logout_redirect_uris?: string[] }) =>
            updateAdminApp(config.apiBase, accessToken as string, vm.code, body),
        onSuccess: (updated) => {
            invalidateApps();
            // EAA-1709: a check run against the old URIs says nothing about the new ones.
            vm.approval.invalidate();
            toast.success(t('console.appDetail.connection.saveSuccess'));
            provisioningResult.handleResult(updated);
        },
        onError: () => toast.error(t('console.appDetail.connection.saveError')),
    });

    const hmacMutation = useMutation({
        mutationFn: () =>
            provisionAppExternalSystem(config.apiBase, accessToken as string, vm.code),
        onSuccess: (data) => {
            invalidateApps();
            // `adopted` returns no secret on purpose (an existing row was linked,
            // not rotated). There is nothing to reveal, so surfacing the server's
            // own explanation as a toast is the whole response — opening a reveal
            // panel with a blank value would read as a bug.
            if (!data.shared_secret) {
                toast.success(data.warning);
                return;
            }
            setRevealedHmac({
                fields: [
                    { label: t('console.appDetail.connection.hmac.systemCode'), value: data.system_code },
                    {
                        label: t('console.appDetail.connection.hmac.sharedSecret'),
                        value: data.shared_secret,
                        wrap: true,
                    },
                ],
                warning: data.warning,
                rotated: data.rotated,
                overlapExpiresAt: data.previous_secret_expires_at,
            });
        },
        onError: (error) =>
            toast.error(
                provisioningDetail(error) ?? t('console.appDetail.connection.hmac.error')
            ),
    });

    const confirmRotationMutation = useMutation({
        mutationFn: (systemCode: string) =>
            confirmAppSecretRotation(config.apiBase, accessToken as string, systemCode),
        onSuccess: () => {
            invalidateApps();
            toast.success(t('console.appDetail.connection.hmac.confirmSuccess'));
        },
        onError: () => toast.error(t('console.appDetail.connection.hmac.confirmError')),
    });

    if (!app) return null;

    const endpoints = oidcEndpointsFor(config.authority);
    const hmac = app.external_system;
    const canIssue = vm.canManageCredentials;
    const approval = vm.approval;
    const overlapOpen = Boolean(
        hmac?.previous_secret_expires_at &&
            new Date(hmac.previous_secret_expires_at).getTime() > Date.now()
    );

    function formatDateTime(value: string) {
        return new Date(value).toLocaleString('vi-VN');
    }

    // Everything the receiving team needs in one block. Built from real fields
    // only — the HMAC lines appear once a credential exists, and the secrets
    // themselves are never in here (they are one-time reveals, by contract).
    const snippet = [
        `# ${t('console.appDetail.connection.pasteCardTitle')} — ${app.name} (${app.code})`,
        '',
        '## OIDC',
        `issuer:        ${config.authority}`,
        `discovery:     ${endpoints.discovery}`,
        ...(app.app_type !== 'api'
            ? [`client_id:     ${app.kc_client_id || '(chưa cấp / not issued)'}  (public, PKCE)`]
            : []),
        ...(app.app_type !== 'web'
            ? [`api_client_id: ${app.kc_api_client_id || '(chưa cấp / not issued)'}  (confidential)`]
            : []),
        `audience:      ${app.audience || app.code}`,
        `redirect_uris: ${(app.redirect_uris ?? []).join(', ') || '—'}`,
        `post_logout:   ${(app.post_logout_redirect_uris ?? []).join(', ') || '—'}`,
        '',
        '## HMAC (server-to-server)',
        `system_code:   ${hmac?.system_code ?? '(chưa cấp / not issued)'}`,
        'headers:       X-External-System-Id, X-HMAC-Timestamp, X-HMAC-Signature',
        'signature:     HMAC-SHA256(secret, "{METHOD}.{path}.{timestamp}.{sha256(body)}")',
        'timestamp:     Unix seconds, ±300s',
    ].join('\n');

    return (
        <div className={detail.grid}>
            <div className={detail.gridMain}>
                <AppDetailClientsCard app={app} issuer={config.authority} canIssue={canIssue} />

                <SectionCard title={t('console.appDetail.connection.redirectCardTitle')}>
                    <p className={detail.hint}>{t('console.appDetail.connection.redirectIntro')}</p>
                    <AppUriListField
                        legend={t('console.appDetail.connection.redirectLegend')}
                        values={redirectUris}
                        onChange={setRedirectUris}
                        onBlur={() => saveMutation.mutate({ redirect_uris: redirectUris.filter(Boolean) })}
                        placeholder="https://…/auth/callback"
                        addLabel={t('console.appDetail.connection.addRedirect')}
                    />
                    <AppUriListField
                        legend={t('console.appDetail.connection.postLogoutLegend')}
                        values={postLogoutUris}
                        onChange={setPostLogoutUris}
                        onBlur={() =>
                            saveMutation.mutate({
                                post_logout_redirect_uris: postLogoutUris.filter(Boolean),
                            })
                        }
                        placeholder="https://…/logged-out"
                        addLabel={t('console.appDetail.connection.addPostLogout')}
                    />
                </SectionCard>

                <SectionCard title={t('console.appDetail.connection.hmac.cardTitle')}>
                    <p className={detail.hint}>{t('console.appDetail.connection.hmac.intro')}</p>
                    <ValueRow
                        label={t('console.appDetail.connection.hmac.systemCode')}
                        value={hmac?.system_code ?? null}
                    />
                    {hmac ? (
                        <>
                            <div className={styles.codeField}>
                                <span className={styles.codeFieldLabel}>
                                    {t('console.appDetail.connection.hmac.sharedSecret')}
                                </span>
                                <span className={styles.codeFieldBox}>
                                    <code className={styles.codeFieldValue}>{'•'.repeat(24)}</code>
                                    <span className={styles.codeFieldMeta}>
                                        {hmac.secret_rotated_at
                                            ? t('console.appDetail.connection.secretRotatedAt', {
                                                  date: new Date(
                                                      hmac.secret_rotated_at
                                                  ).toLocaleDateString('vi-VN'),
                                              })
                                            : t('console.appDetail.connection.hmac.issuedNeverRotated')}
                                    </span>
                                </span>
                            </div>
                            {!hmac.is_active && (
                                <Callout tone="danger">
                                    {t('console.appDetail.connection.hmac.inactive')}
                                </Callout>
                            )}
                        </>
                    ) : (
                        <Callout tone="info">
                            {t('console.appDetail.connection.hmac.notProvisioned')}
                        </Callout>
                    )}

                    {overlapOpen && hmac?.previous_secret_expires_at && (
                        <Callout
                            tone="warning"
                            label={t('console.appDetail.connection.hmac.overlapLabel')}
                            longLabel
                        >
                            {t('console.appDetail.connection.hmac.overlapBody', {
                                time: formatDateTime(hmac.previous_secret_expires_at),
                            })}
                        </Callout>
                    )}

                    {revealedHmac ? (
                        <AppCredentialReveal
                            fields={revealedHmac.fields}
                            warning={revealedHmac.warning}
                            rotated={revealedHmac.rotated}
                            onCopy={copy}
                            onDismiss={() => setRevealedHmac(null)}
                        >
                            {revealedHmac.overlapExpiresAt && (
                                <p className={detail.hint}>
                                    {t('console.appDetail.connection.hmac.overlapBody', {
                                        time: formatDateTime(revealedHmac.overlapExpiresAt),
                                    })}
                                </p>
                            )}
                        </AppCredentialReveal>
                    ) : (
                        canIssue && (
                            <div className={detail.buttonRow}>
                                {hmac ? (
                                    <ConfirmButton
                                        danger
                                        label={
                                            hmacMutation.isPending
                                                ? t('console.appDetail.connection.hmac.rotating')
                                                : t('console.appDetail.connection.hmac.rotateButton')
                                        }
                                        disabled={hmacMutation.isPending}
                                        confirmTitle={t(
                                            'console.appDetail.connection.hmac.rotateConfirmTitle'
                                        )}
                                        confirmDescription={t(
                                            'console.appDetail.connection.hmac.rotateConfirmBody'
                                        )}
                                        onConfirm={() => hmacMutation.mutate()}
                                    />
                                ) : (
                                    <Button
                                        type="primary"
                                        onClick={() => hmacMutation.mutate()}
                                        disabled={hmacMutation.isPending}
                                    >
                                        {hmacMutation.isPending
                                            ? t('console.appDetail.connection.hmac.creating')
                                            : t('console.appDetail.connection.hmac.createButton')}
                                    </Button>
                                )}
                                {overlapOpen && hmac && (
                                    <ConfirmButton
                                        label={
                                            confirmRotationMutation.isPending
                                                ? t('console.appDetail.connection.hmac.confirming')
                                                : t('console.appDetail.connection.hmac.confirmButton')
                                        }
                                        disabled={confirmRotationMutation.isPending}
                                        confirmTitle={t(
                                            'console.appDetail.connection.hmac.confirmConfirmTitle'
                                        )}
                                        confirmDescription={t(
                                            'console.appDetail.connection.hmac.confirmConfirmBody'
                                        )}
                                        onConfirm={() =>
                                            confirmRotationMutation.mutate(hmac.system_code)
                                        }
                                    />
                                )}
                            </div>
                        )
                    )}

                    <details className={styles.recipe}>
                        <summary className={styles.recipeSummary}>
                            {t('console.appDetail.connection.hmac.recipeToggle')}
                        </summary>
                        <p className={detail.hint}>
                            {t('console.appDetail.connection.hmac.recipeIntro')}
                        </p>
                        <pre className={styles.codeBlock}>
                            {[
                                'X-External-System-Id: <system_code>',
                                'X-HMAC-Timestamp:     <unix seconds, ±300s>',
                                'X-HMAC-Signature:     <hex digest below>',
                                '',
                                'canonical = "{METHOD}.{path_with_query}.{timestamp}.{sha256_hex(body)}"',
                                'signature = hmac_sha256(shared_secret, canonical).hexdigest()',
                            ].join('\n')}
                        </pre>
                    </details>
                </SectionCard>
            </div>

            <div className={detail.gridSide}>
                <SectionCard title={t('console.appDetail.connection.endpointsCardTitle')}>
                    <p className={detail.hint}>
                        {t('console.appDetail.connection.endpointsIntro')}
                    </p>
                    <ValueRow
                        label={t('console.appDetail.connection.endpointDiscovery')}
                        value={endpoints.discovery}
                    />
                    <ValueRow
                        label={t('console.appDetail.connection.endpointAuthorization')}
                        value={endpoints.authorization}
                    />
                    <ValueRow
                        label={t('console.appDetail.connection.endpointToken')}
                        value={endpoints.token}
                    />
                    <ValueRow
                        label={t('console.appDetail.connection.endpointUserinfo')}
                        value={endpoints.userinfo}
                    />
                    <ValueRow
                        label={t('console.appDetail.connection.endpointJwks')}
                        value={endpoints.jwks}
                    />
                    <ValueRow
                        label={t('console.appDetail.connection.endpointEndSession')}
                        value={endpoints.endSession}
                    />
                </SectionCard>

                <SectionCard title={t('console.appDetail.connection.pasteCardTitle')}>
                    <p className={detail.hint}>{t('console.appDetail.connection.pasteIntro')}</p>
                    <pre className={styles.codeBlock} data-testid="app-detail-code-block">
                        {snippet}
                    </pre>
                    <div className={detail.buttonRow}>
                        <Button onClick={() => copy(snippet)}>
                            {t('console.appDetail.connection.copyAction')}
                        </Button>
                    </div>
                </SectionCard>

                {canIssue && (
                    <SectionCard title={t('console.appDetail.connection.check.cardTitle')}>
                        <p className={detail.hint}>
                            {t('console.appDetail.connection.check.intro')}
                        </p>
                        <div className={detail.buttonRow}>
                            <Button onClick={approval.runCheck} disabled={approval.checking}>
                                {approval.checking
                                    ? t('console.appDetail.connection.check.pending')
                                    : t('console.appDetail.connection.check.button')}
                            </Button>
                        </div>
                        {approval.report && <ConnectionCheckResult report={approval.report} />}
                    </SectionCard>
                )}
            </div>

            <AppProvisioningResultModal
                provisioning={provisioningResult.provisioning}
                onClose={provisioningResult.close}
            />
        </div>
    );
}

export default AppDetailConnectionTab;
